import { CORE_CONSTANT, type ViewConfigAdditionalConfig, type ViewConfigKpiParameter } from '@salescore/core'
import { localStorageEffect } from '@salescore/frontend-common'
import { Array } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { atom, selector } from 'recoil'
import { z } from 'zod'

import { additionalConfigAtom } from '../records/atoms'
import { configAtom } from '../viewConfig'

const prefix = `view/navigation`

export const sheetPickedPresetNameAtom = atom<string | undefined>({
  key: `${prefix}/sheetPickedPresetNameAtom`,
  default: undefined,
})

// viewIdを指定してドリルダウン
export interface DrillDownParameterForAny {
  type: 'any'
  viewId: string
}

// recordIdを指定して、フォームとして表示
export interface DrillDownParameterForSheet {
  type: 'form'
  recordId: string
  // 本来はここでフォームのviewIdを指定、ないしモデル名を指定したい
  // viewId
  // 現状だとフォーム周りの実装をしておらず、formのviewは存在しないので、prevViewがsheetである前提で作っている
}

// ダッシュボード・KPIからのドリルダウン。KPIと行列の値(絞り込みで利用)を指定して表示
export interface DrillDownParameterForKpiPivot {
  type: 'kpi'
  dimensionValues: {
    rows: Array<{ value: unknown; label: string | undefined }>
    columns: Array<{ value: unknown; label: string | undefined }>
  }
  viewId?: string
  additionalFilterLeafsWithPrev?: ViewConfigAdditionalConfig['filterLeafs']
  queryParameter?: ViewConfigKpiParameter['queryParameter']
  kpiPivotPresetName?: string
}

interface ModalAtomTypeWithInnerState {
  visible: boolean
  content: DrillDownParameter | undefined
  innerState: {
    hasChanges: boolean // ドリルダウンモーダルに未保存のデータ変更があるか
  }
}

export type DrillDownParameter = DrillDownParameterForSheet | DrillDownParameterForKpiPivot | DrillDownParameterForAny
export const drillDownModalAtom = atom<ModalAtomTypeWithInnerState>({
  key: `${prefix}/drillDownModal`,
  default: {
    visible: false,
    content: undefined,
    innerState: { hasChanges: false },
  },
})

// 以下、kpi系(別にした方がよいだろうか？)
export const kpiParameterAtom = selector<ViewConfigKpiParameter>({
  key: `${prefix}/kpiParameterAtom`,
  get({ get }) {
    const additionalConfig = get(additionalConfigAtom)
    // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
    const config = get(configAtom)
    return additionalConfig.kpiParameter ?? CORE_CONSTANT.PIVOT_EMPTY_PRESET().parameter
  },
  set({ get, set }, value) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    const kpiParameter = value as ViewConfigKpiParameter
    const getUniqueKey = (x: { key: string; dateSpan?: string }): string =>
      [x.key, x.dateSpan].filter(isNotNullish).join(',')
    set(additionalConfigAtom, (x) => ({
      ...x,
      kpiParameter: {
        ...kpiParameter,
        pivot: {
          rows: Array.dedupeWith(kpiParameter.pivot.rows, (a, b) => getUniqueKey(a) === getUniqueKey(b)), // XXX: 不要なはずだが、どこかのロジックでKPIが重複することがあったので一応追加
          columns: Array.dedupeWith(kpiParameter.pivot.columns, (a, b) => getUniqueKey(a) === getUniqueKey(b)),
          dimensionsWithoutRollup:
            kpiParameter.pivot.dimensionsWithoutRollup === undefined
              ? kpiParameter.pivot.dimensionsWithoutRollup
              : Array.dedupeWith(
                  kpiParameter.pivot.dimensionsWithoutRollup,
                  (a, b) => getUniqueKey(a) === getUniqueKey(b),
                ),
          columnsForDrilldownComments:
            kpiParameter.pivot.columnsForDrilldownComments === undefined
              ? kpiParameter.pivot.columnsForDrilldownComments
              : Array.dedupeWith(
                  kpiParameter.pivot.columnsForDrilldownComments,
                  (a, b) => getUniqueKey(a) === getUniqueKey(b),
                ),
        },
        // periodだけは既存の設定を引き継ぎたいので以下のように上書き
        queryParameter: kpiParameter.queryParameter ?? x.kpiParameter?.queryParameter,
      },
    }))
  },
})

export const kpiPivotPickedPresetNameAtom = atom<string | undefined>({
  key: `${prefix}/kpiPivotPickedPresetNameAtom`,
  default: undefined,
})

// ユーザーごとのちょっとした表示設定(=組織全体に反映しない設定)
const kpiUserAppearanceSchema = z.object({
  diffVisible: z.boolean().optional(),
  goalVisible: z.boolean().optional(),
  perElapsedBusinessDays: z.boolean().optional(),
  colorSchema: z.enum(['normal', 'success-green']).optional(),
  skipTotal: z.boolean().optional(),
  totalRowOrder: z.enum([`top`, `bottom`]).optional(),
  totalColumnOrder: z.enum([`left`, `right`]).optional(),
  fontSize: z.number().optional(),
  unlimitedRowsEnabled: z.boolean().optional(),
  unlimitedColumnsEnabled: z.boolean().optional(),
})
export type KpiUserAppearance = z.infer<typeof kpiUserAppearanceSchema>
export const kpiUserAppearanceAtom = atom<KpiUserAppearance>({
  key: `${prefix}/kpiUserAppearanceAtom`,
  default: {
    diffVisible: true,
    goalVisible: true,
  },
  effects: [localStorageEffect(`${prefix}/kpiUserAppearanceAtom`, kpiUserAppearanceSchema)],
})
